import { AddOutlined, DeleteOutlined, EditOutlined, RemoveOutlined } from "@mui/icons-material";
import { Button, Card, Collapse, Divider, Stack, styled, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { ExternalAccessDto } from "../../../Core/Api/Types/access-models";
import { useToggle } from "../../../Utils/Hooks/useToggle";
import RotatableBox from "../Shared/rotatable-box";
import ExternalAccessPermissionSummary from "./external-access-permission-summary";

interface ExternalAccessCardProps {
  access: ExternalAccessDto;
  creatorUserName: string;
  userCanEditAccess: boolean;
  userCanRevokeAccess: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onMouseEnterCard?: () => void;
  onMouseLeaveCard?: () => void;
}

const ExternalAccessCard = (props: ExternalAccessCardProps) => {
  const { t } = useTranslation();
  const {
    access,
    creatorUserName,
    userCanEditAccess,
    userCanRevokeAccess,
    onEdit,
    onDelete,
    onMouseEnterCard,
    onMouseLeaveCard,
  } = props;
  const { active: isExpanded, toggle: toggleExpanded } = useToggle(false);

  const nbDaysTillExpiration = React.useMemo(() => {
    const accessDate = new Date(access.expirationDate);
    const currentDate = new Date();
    const diffTime = accessDate.getTime() - currentDate.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }, [access.expirationDate]);

  const handleEditClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    onEdit();
  };

  const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    onDelete();
  };

  return (
    <StyledCard onClick={toggleExpanded} onMouseEnter={onMouseEnterCard} onMouseLeave={onMouseLeaveCard}>
      <Stack spacing={0.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack spacing={0.5}>
            <Typography variant="body1">{access.objectName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("component:body.emailAndItemsWithCount", { email: access.email, count: access.objectChildCount || 0 })}
            </Typography>
          </Stack>
          <RotatableBox rotated={isExpanded} from={0} to={180}>
            {!isExpanded ? <AddOutlined /> : <RemoveOutlined />}
          </RotatableBox>
        </Stack>
        <ExternalAccessPermissionSummary permissions={access.permissions} showTooltip />
        <Collapse in={isExpanded}>
          <Divider sx={{ mx: -2, mt: 0.5 }} />
          <Stack spacing={0.5} my={0.5}>
            <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" pt={0.5}>
              <Stack>
                <Typography fontSize={11} color="text.tier">
                  {t("common:createdBy")}
                </Typography>
                <Typography variant="body2">{creatorUserName}</Typography>
              </Stack>
              <Stack>
                <Typography fontSize={11} color="text.tier">
                  {t("table:headerName.expiresIn")}
                </Typography>
                <Typography variant="body2" color="warning.main">
                  {nbDaysTillExpiration} {t("common:days")}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                {userCanEditAccess && (
                  <Button onClick={handleEditClick}>
                    <EditOutlined />
                  </Button>
                )}
                {userCanRevokeAccess && (
                  <Button onClick={handleDeleteClick}>
                    <DeleteOutlined color="error" />
                  </Button>
                )}
              </Stack>
            </Stack>
          </Stack>
        </Collapse>
      </Stack>
    </StyledCard>
  );
};

const StyledCard = styled(Card)(({ theme }) => ({
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(0.5),
  width: "100%",
  borderRadius: 8,
  backgroundColor: "transparent",
  border: "1px solid",
  borderColor: theme.palette.backgroundAlpha.border,
  "&:hover": {
    background: theme.palette.backgroundAlpha.hover,
    transition: theme.transitions.create(["background"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
}));

export default ExternalAccessCard;
